import { Trash2, Plus, Minus, ShoppingCart, X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { WhatsAppButton } from './WhatsAppButton';
import { useState } from 'react';

const PLACEHOLDER_IMAGE = "https://images.unsplash.com/photo-1764025390519-1ccc15d719a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHx3b29kJTIwcGxhbmtzJTIwcmF3JTIwbHVtYmVyfGVufDB8fHx8MTc3MDE1Nzc4OXww&ixlib=rb-4.1.0&q=85&w=100";

const formatPrice = (price) => {
    if (!price && price !== 0) return null;
    return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(price);
};

// Get price from product - handles new API structure
const getProductPrice = (product) => {
    // Direct precio field
    if (product.precio) return product.precio;
    // Legacy precios array
    if (!product.precios || product.precios.length === 0) return null;
    const firstPrice = product.precios[0];
    if (typeof firstPrice === 'number') return firstPrice;
    if (typeof firstPrice === 'object' && firstPrice?.precio) return firstPrice.precio;
    return null;
};

const CartItem = ({ item }) => {
    const { updateQuantity, removeItem } = useCart();
    const price = getProductPrice(item);
    const [inputValue, setInputValue] = useState(item.quantity.toString());

    const getImageSrc = () => {
        if (!item.foto) return PLACEHOLDER_IMAGE;
        if (item.foto.startsWith('data:')) return item.foto;
        return item.foto;
    };

    const handleQuantityChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        const num = parseInt(value);
        if (!isNaN(num) && num > 0) {
            updateQuantity(item.product_id, num);
        }
    };

    const handleBlur = () => {
        const num = parseInt(inputValue);
        if (isNaN(num) || num < 1) {
            setInputValue(item.quantity.toString());
        }
    };

    const handleDecrement = () => {
        const newQty = item.quantity - 1;
        if (newQty > 0) {
            updateQuantity(item.product_id, newQty);
            setInputValue(newQty.toString());
        }
    };

    const handleIncrement = () => {
        const newQty = item.quantity + 1;
        updateQuantity(item.product_id, newQty);
        setInputValue(newQty.toString());
    };

    return (
        <div className="cart-item" data-testid={`cart-item-${item.product_id}`}>
            <img
                src={getImageSrc()}
                alt={item.titulo || item.nombre}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-cover bg-stone-100 flex-shrink-0"
                onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
            />
            <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-stone-900 text-sm sm:text-base line-clamp-2">
                    {item.titulo || item.nombre}
                </h4>
                {item.categoria && (
                    <p className="text-xs text-stone-500 line-clamp-1 mt-0.5">
                        {item.categoria}
                    </p>
                )}
                {price && (
                    <p className="text-sm font-mono font-bold text-[#007a3f] mt-1">
                        {formatPrice(price)}
                    </p>
                )}
                <div className="flex items-center gap-2 mt-2">
                    <button
                        onClick={handleDecrement}
                        className="quantity-btn h-8 w-8"
                        data-testid={`cart-decrement-${item.product_id}`}
                        aria-label="Reducir cantidad"
                    >
                        <Minus className="h-3 w-3" />
                    </button>
                    <input
                        type="number"
                        min="1"
                        value={inputValue}
                        onChange={handleQuantityChange}
                        onBlur={handleBlur}
                        className="w-12 text-center font-semibold text-sm border border-stone-200 rounded-md py-1"
                        data-testid={`cart-quantity-input-${item.product_id}`}
                    />
                    <button
                        onClick={handleIncrement}
                        className="quantity-btn h-8 w-8"
                        data-testid={`cart-increment-${item.product_id}`}
                        aria-label="Aumentar cantidad"
                    >
                        <Plus className="h-3 w-3" />
                    </button>
                </div>
            </div>
            <button
                onClick={() => removeItem(item.product_id)}
                className="p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors tap-feedback self-start"
                data-testid={`cart-remove-${item.product_id}`}
                aria-label="Eliminar producto"
            >
                <Trash2 className="h-4 w-4" />
            </button>
        </div>
    );
};

const EmptyCart = () => (
    <div className="empty-state py-16" data-testid="empty-cart">
        <div className="w-24 h-24 bg-stone-100 rounded-full flex items-center justify-center mb-4">
            <ShoppingCart className="h-10 w-10 text-stone-400" />
        </div>
        <h3 className="text-lg font-semibold text-stone-700 mb-2">
            Tu carrito está vacío
        </h3>
        <p className="text-sm text-stone-500 max-w-xs">
            Agregá productos desde el catálogo para armar tu pedido
        </p>
    </div>
);

export const CartSheet = ({ open, onClose }) => {
    const { items, totalItems, totalPrice, clearCart } = useCart();

    const handleClose = () => {
        if (onClose) onClose(false);
    };

    return (
        <Sheet open={open} onOpenChange={handleClose}>
            <SheetContent 
                className="w-full sm:max-w-md flex flex-col p-0 h-full"
                data-testid="cart-sheet"
                showCloseButton={false}
            >
                <SheetHeader className="px-4 py-4 border-b border-stone-200 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <SheetTitle className="text-xl font-bold text-[#007a3f]">
                            Tu Pedido
                        </SheetTitle>
                        <div className="flex items-center gap-2">
                            {items.length > 0 && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={clearCart}
                                    className="text-stone-500 hover:text-red-500 text-sm"
                                    data-testid="clear-cart-button"
                                >
                                    Vaciar
                                </Button>
                            )}
                            <button
                                onClick={handleClose}
                                className="p-2 hover:bg-stone-100 rounded-full transition-colors"
                                data-testid="close-cart-button"
                                aria-label="Cerrar"
                            >
                                <X className="h-5 w-5 text-stone-500" />
                            </button>
                        </div>
                    </div>
                    <SheetDescription className="sr-only">
                        Carrito de compras con los productos seleccionados
                    </SheetDescription>
                    {totalItems > 0 && (
                        <p className="text-sm text-stone-500 mt-1">
                            {totalItems} {totalItems === 1 ? 'producto' : 'productos'}
                        </p>
                    )}
                </SheetHeader>

                {items.length === 0 ? (
                    <EmptyCart />
                ) : (
                    <>
                        <div className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
                            <div className="px-4 py-2">
                                {items.map(item => (
                                    <CartItem key={item.product_id} item={item} />
                                ))}
                            </div>
                        </div>

                        {/* Footer with total and WhatsApp button */}
                        <div className="border-t border-stone-200 p-4 space-y-4 bg-white">
                            {totalPrice > 0 && (
                                <div className="flex items-center justify-between">
                                    <span className="text-stone-600 font-medium">Total estimado:</span>
                                    <span className="text-xl font-mono font-bold text-[#007a3f]" data-testid="cart-total">
                                        {formatPrice(totalPrice)}
                                    </span>
                                </div>
                            )}
                            <WhatsAppButton items={items} onOrderSent={onClose} />
                            <p className="text-xs text-stone-400 text-center">
                                Te contactaremos para confirmar disponibilidad y precio final
                            </p>
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
};
